import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Typography,
  AppBar,
  Toolbar,
  Drawer,
  Hidden,
  useMediaQuery,
  SwipeableDrawer,
  Breadcrumbs,
  Link,
  Box,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import TooltipIconButton from '../../widgets/TooltipIconButton';
import { seedDashboard3DrawersIcons } from '../../icon/seed-icons';
import D3DTopAppBar from './D3DTopAppBar';
import { dashboard3DrawersIconsPropTypes, drawerRoutesPropTypes, d3dPopoverMenusPropTypes, defaultD3dPopoverMenusPropTypes } from './d3d-prop-types';
import D3DDrawer from './D3DDrawer';
import seedLogo from '../../images/logo.svg';

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    backgroundColor: theme.palette.background.default,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  toolbar: theme.mixins.toolbar,
  drawer: {
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
  },
  drawerPaper: {
    width: drawerWidth,
    // backgroundImage: `url(${Image})`,
    // backgroundPosition: "right bottom",
    // backgroundRepeat: "no-repeat",
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    width: theme.spacing(7) + 1,
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  main: {
    flexGrow: 1,
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    width: '100%',
  },
  content: {
    padding: (props) => (props.usePaddingContent ? theme.spacing(3) : 0),
  },
}));

const Dashboard3Drawers = (props) => {
  const {
    children,
    AuthenticationRepository,

    titleId,
    icons,
    routes,
    isLoading,
    useBackButton,
    backPath,
    backButtonAction,
    usePaddingContent,
    defaultOpenDrawer,
    applicationName,
    logo,
    breadcrumbs,
    popoverMenus,
    popoverProfilePath,
  } = props;
  const classes = useStyles({ usePaddingContent });
  const history = useHistory();

  const [open, setOpen] = React.useState(defaultOpenDrawer);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const handleDrawerMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOnToggleMobileDrawer = (newValue) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setMobileOpen(newValue);
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleClickBack = () => {
    if (history.length > 2) {
      history.goBack();
    } else {
      history.replace(backPath);
    }
  };

  return (
    <div className={classes.root}>
      <D3DTopAppBar
        AuthenticationRepository={AuthenticationRepository}
        isLoading={isLoading}
        onDrawerToggle={handleDrawerToggle}
        onDrawerToggleMobile={handleDrawerMobileToggle}
        icons={icons}
        titleId={titleId}
        logo={logo}
        useBackButton={useBackButton}
        backButtonAction={backButtonAction}
        onClickBack={handleClickBack}
        applicationName={applicationName}
        popoverMenus={popoverMenus}
        popoverProfilePath={popoverProfilePath}
      />
      <nav aria-label="mailbox folders">
        <Hidden smUp implementation="css">
          <SwipeableDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleOnToggleMobileDrawer(false)}
            onOpen={handleOnToggleMobileDrawer(true)}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true,
            }}
          >
            <D3DDrawer
              useBanner
              routes={routes}
              logo={logo}
              applicationName={applicationName}
              icons={icons}
              showNested
            />
          </SwipeableDrawer>
        </Hidden>
        <Hidden only={['lg', 'xl', 'xs']} implementation="css">
          <Drawer
            className={clsx(classes.drawer, classes.drawerClose)}
            classes={{
              paper: classes.drawerClose,
            }}
            variant="permanent"
            open={open}
          >
            <D3DDrawer
              routes={routes}
              logo={logo}
              applicationName={applicationName}
              useTooltip
              icons={icons}
            />
          </Drawer>

        </Hidden>
        <Hidden mdDown implementation="css">
          <Drawer
            className={clsx(classes.drawer, {
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            })}
            classes={{
              paper: clsx(classes.drawerPaper, {
                [classes.drawerOpen]: open,
                [classes.drawerClose]: !open,
              }),
            }}
            variant="permanent"
            open={open}
          >
            <D3DDrawer
              routes={routes}
              logo={logo}
              applicationName={applicationName}
              useTooltip={!open}
              icons={icons}
              showNested={open}
            />
          </Drawer>
        </Hidden>
      </nav>
      <main className={classes.main}>
        <div className={classes.toolbar} />
        {
          useBackButton && !isMobile
            ? (
              <AppBar position="relative" color="default" component="div">
                <Toolbar>
                  <TooltipIconButton
                    edge="start"
                    onClick={handleClickBack}
                    className={clsx([classes.menuButton])}
                    icon={<icons.ArrowBack />}
                    title={<FormattedMessage id="seed.button.back" />}
                  />
                  <Typography variant="h6" noWrap>
                    <FormattedMessage id={titleId} />
                  </Typography>
                </Toolbar>
              </AppBar>
            ) : ''
        }
        {
          breadcrumbs.length <= 0 ? '' : (
            <Box pt={1} pl={3} pr={3}>
              <Breadcrumbs aria-label="breadcrumb">
                {
                  breadcrumbs.map((bread) => {
                    if (bread.href) {
                      return (
                        <Link
                          key={`D3D-breadcrumb-${bread.id}`}
                          color="inherit"
                          href={bread.href}
                        >
                          <Typography variant="subtitle2">
                            {
                              bread.titleId ? (
                                <FormattedMessage id={bread.titleId} />
                              ) : bread.title
                            }
                          </Typography>
                        </Link>
                      );
                    }
                    return (
                      <Typography
                        key={`D3D-breadcrumb-${bread.id}`}
                        variant="subtitle2"
                        color="textPrimary"
                      >
                        {
                          bread.titleId ? (
                            <FormattedMessage id={bread.titleId} />
                          ) : bread.title
                        }
                      </Typography>
                    );
                  })
                }
              </Breadcrumbs>
            </Box>
          )
        }
        <div className={classes.content}>
          {children}
        </div>
      </main>
    </div>
  );
};

Dashboard3Drawers.propTypes = {
  children: PropTypes.node.isRequired,
  titleId: PropTypes.string.isRequired,
  AuthenticationRepository: PropTypes.func.isRequired,

  routes: drawerRoutesPropTypes,
  icons: dashboard3DrawersIconsPropTypes,
  isLoading: PropTypes.bool,
  useBackButton: PropTypes.bool,
  backButtonAction: PropTypes.node,
  backPath: PropTypes.string,
  usePaddingContent: PropTypes.bool,
  defaultOpenDrawer: PropTypes.bool,
  applicationName: PropTypes.string.isRequired,
  logo: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      titleId: PropTypes.string,
      href: PropTypes.string,
    }),
  ),
  popoverMenus: d3dPopoverMenusPropTypes,
  popoverProfilePath: PropTypes.string,
};

Dashboard3Drawers.defaultProps = {
  routes: [],
  icons: seedDashboard3DrawersIcons,
  isLoading: false,
  useBackButton: false,
  backButtonAction: undefined,
  backPath: '/',
  usePaddingContent: true,
  defaultOpenDrawer: true,
  logo: seedLogo,
  breadcrumbs: [],
  popoverMenus: defaultD3dPopoverMenusPropTypes,
  popoverProfilePath: undefined,
};

export default Dashboard3Drawers;
